import { Box, Card, CardContent, Typography, Button } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

function TermsOfService() {
  return (
    <>
      <Typography variant="h5" fontWeight={700} gutterBottom>Terms of Service</Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>Last updated: March 14, 2026</Typography>

      <Typography variant="h6" mt={3} mb={1}>1. Acceptance of Terms</Typography>
      <Typography variant="body2">
        By accessing or using AirScribe ("the Service"), you agree to be bound by these Terms of Service. If you do not agree, do not use the Service.
      </Typography>

      <Typography variant="h6" mt={3} mb={1}>2. Description of Service</Typography>
      <Typography variant="body2">
        AirScribe provides AI-assisted medical transcription and SOAP note generation for healthcare professionals. The Service is intended as a productivity tool and does not replace professional medical judgment.
      </Typography>

      <Typography variant="h6" mt={3} mb={1}>3. User Accounts</Typography>
      <Typography variant="body2">
        You are responsible for maintaining the confidentiality of your account credentials and for all activities under your account. You must provide accurate information when creating an account.
      </Typography>

      <Typography variant="h6" mt={3} mb={1}>4. Acceptable Use</Typography>
      <Typography variant="body2">
        You agree not to misuse the Service, including but not limited to: reverse engineering, unauthorized access attempts, using the Service for unlawful purposes, or sharing account credentials.
      </Typography>

      <Typography variant="h6" mt={3} mb={1}>5. Medical Disclaimer</Typography>
      <Typography variant="body2">
        The Service uses AI to assist with transcription and note generation. All outputs should be reviewed by a qualified healthcare professional before use in patient care. AirScribe is not responsible for clinical decisions made based on AI-generated content.
      </Typography>

      <Typography variant="h6" mt={3} mb={1}>6. Intellectual Property</Typography>
      <Typography variant="body2">
        The Service and its original content, features, and functionality are owned by AirScribe and are protected by copyright, trademark, and other intellectual property laws. You retain ownership of your patient data and notes.
      </Typography>

      <Typography variant="h6" mt={3} mb={1}>7. Termination</Typography>
      <Typography variant="body2">
        We may terminate or suspend your account at any time for violations of these Terms. Upon termination, your right to use the Service ceases immediately. You may export your data before termination.
      </Typography>

      <Typography variant="h6" mt={3} mb={1}>8. Limitation of Liability</Typography>
      <Typography variant="body2">
        To the maximum extent permitted by law, AirScribe shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of the Service.
      </Typography>

      <Typography variant="h6" mt={3} mb={1}>9. Changes to Terms</Typography>
      <Typography variant="body2">
        We reserve the right to modify these Terms at any time. Continued use of the Service after changes constitutes acceptance of the updated Terms.
      </Typography>

      <Typography variant="h6" mt={3} mb={1}>10. Contact</Typography>
      <Typography variant="body2">
        For questions about these Terms, please contact us at support@airscribe.app.
      </Typography>
    </>
  );
}

function PrivacyPolicy() {
  return (
    <>
      <Typography variant="h5" fontWeight={700} gutterBottom>Privacy Policy</Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>Last updated: March 14, 2026</Typography>

      <Typography variant="h6" mt={3} mb={1}>1. Information We Collect</Typography>
      <Typography variant="body2">
        We collect information you provide directly: account details (email, phone number, name), patient encounter data you enter or transcribe, and SOAP notes generated through the Service.
      </Typography>

      <Typography variant="h6" mt={3} mb={1}>2. How We Use Your Information</Typography>
      <Typography variant="body2">
        We use your information to: provide and maintain the Service, process audio transcriptions, generate SOAP notes, authenticate your identity, and communicate with you about the Service.
      </Typography>

      <Typography variant="h6" mt={3} mb={1}>3. Audio Data</Typography>
      <Typography variant="body2">
        Audio recordings are processed in real-time for transcription and are not permanently stored on our servers. Transcribed text is stored in your account for note generation purposes.
      </Typography>

      <Typography variant="h6" mt={3} mb={1}>4. Data Storage and Security</Typography>
      <Typography variant="body2">
        Your data is stored securely using industry-standard encryption. We implement appropriate technical and organizational measures to protect your personal and patient data against unauthorized access, alteration, or destruction.
      </Typography>

      <Typography variant="h6" mt={3} mb={1}>5. HIPAA Compliance</Typography>
      <Typography variant="body2">
        We are committed to handling protected health information (PHI) in accordance with HIPAA requirements. We implement administrative, physical, and technical safeguards to ensure the confidentiality, integrity, and availability of PHI.
      </Typography>

      <Typography variant="h6" mt={3} mb={1}>6. Third-Party Services</Typography>
      <Typography variant="body2">
        We use third-party services for authentication (Firebase), transcription (ElevenLabs), and AI processing. These services process data in accordance with their respective privacy policies and our data processing agreements.
      </Typography>

      <Typography variant="h6" mt={3} mb={1}>7. Data Retention</Typography>
      <Typography variant="body2">
        We retain your account data and notes for as long as your account is active. You may request deletion of your data at any time by contacting us. Upon account deletion, your data will be permanently removed within 30 days.
      </Typography>

      <Typography variant="h6" mt={3} mb={1}>8. Your Rights</Typography>
      <Typography variant="body2">
        You have the right to: access your personal data, correct inaccurate data, request deletion of your data, export your data, and opt out of non-essential communications.
      </Typography>

      <Typography variant="h6" mt={3} mb={1}>9. Children's Privacy</Typography>
      <Typography variant="body2">
        The Service is not intended for use by individuals under 18 years of age. We do not knowingly collect personal information from children.
      </Typography>

      <Typography variant="h6" mt={3} mb={1}>10. Changes to This Policy</Typography>
      <Typography variant="body2">
        We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date.
      </Typography>

      <Typography variant="h6" mt={3} mb={1}>11. Contact</Typography>
      <Typography variant="body2">
        For questions about this Privacy Policy, please contact us at privacy@airscribe.app.
      </Typography>
    </>
  );
}

export default function LegalPage({ page }: { page: 'tos' | 'privacy' }) {
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Card sx={{ maxWidth: 600, width: '100%' }}>
        <CardContent>
          <Button
            size="small" startIcon={<ArrowBackIcon />}
            onClick={() => window.history.back()}
            sx={{ mb: 2 }}
          >
            Back
          </Button>
          {page === 'tos' ? <TermsOfService /> : <PrivacyPolicy />}
        </CardContent>
      </Card>
    </Box>
  );
}
